<template>
  <div class="configurator-layout">
    <div class="configurator-layout__header">
      <slot name="header"></slot>
    </div>

    <transition name="configurator-layout__sidebar-">
      <div :key="'sidebar'" class="configurator-layout__sidebar">
        <div class="configurator-layout__sidebar-container">
          <slot name="sidebar"></slot>
        </div>
      </div>
    </transition>

    <transition name="configurator-layout__content-">
      <div :key="'content'" class="configurator-layout__content">
        <div class="configurator-layout__content-container">
          <slot name="content"></slot>
        </div>
      </div>
    </transition>

    <div class="configurator-layout__footer">
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script>
import { Breakpoint } from '../../utils/breakpoints';
import { LayoutView } from '../../store/modules/configurator/layout';

export default {
  name: 'ConfiguratorLayout',
  data() {
    return {
      LayoutView,
    };
  },
  computed: {
    activeView() {
      return this.$store.state.configurator.layout.activeView;
    },
    isSmallScreen() {
      return !this.$matchMedia[Breakpoint.CONFIGURATOR_L];
    },
  },
};
</script>

<style lang="scss" scoped>
.configurator-layout {
  $block-selector: &;
  $base-layout-breakpoint: $breakpoint-l;
  $base-layout-gutter: 2em;
  $base-layout-columns-l: 0 1fr 1fr 0;
  $base-layout-rows: 0 3.5rem 1fr min-content 0;
  $base-layout-sidebar-transition-duration: $default-transition-duration;
  $base-layout-content-transition-duration: $default-transition-duration;

  max-width: $breakpoint-full-hd;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 3.5rem 1fr min-content;
  grid-template-areas:
    'header'
    'body'
    'footer';

  @include breakpoint($base-layout-breakpoint) {
    grid-template-columns: 0 1fr minmax(32em, 1fr) 0;
    grid-template-rows: $base-layout-rows;
    gap: 1em;
    grid-template-areas:
      '. . . .'
      '. header header .'
      '. sidebar content .'
      '. sidebar content .'
      '. . . .';
  }

  @include breakpoint($breakpoint-xl) {
    grid-template-columns: 0 1fr minmax(40em, 1fr) 0;
    gap: $base-layout-gutter;
  }

  &__header {
    grid-area: header;
  }

  &__sidebar,
  &__content {
    display: flex;
    flex-direction: column;
  }

  &__sidebar-container,
  &__content-container {
    flex: 1;
    min-height: 0;
  }

  &__sidebar {
    z-index: 300;
    pointer-events: none;
    position: relative;
    grid-area: body;
    overflow: hidden;
    // Add transition on the root element so <transition> can detect the end of the transition
    transition: visibility 0s $base-layout-sidebar-transition-duration;

    @include breakpoint($base-layout-breakpoint) {
      grid-area: sidebar;
    }
  }

  &__sidebar-container {
    display: flex;
    flex-direction: column;
    height: 100%;

    #{$block-selector}__sidebar--enter &,
    #{$block-selector}__sidebar--leave-to & {
      transform: translateY(100%);
    }

    #{$block-selector}__sidebar--enter-active &,
    #{$block-selector}__sidebar--leave-active & {
      transition: transform $base-layout-sidebar-transition-duration;
    }

    @include breakpoint($base-layout-breakpoint) {
      display: flex;
      align-items: center;
    }
  }

  &__content {
    position: relative;
    grid-area: body;
    overflow: hidden;
    // Add transition on the root element so <transition> can detect the end of the transition
    transition: visibility 0s $base-layout-content-transition-duration;

    @include breakpoint($base-layout-breakpoint) {
      grid-area: content;
      overflow: auto;
    }
  }

  &__content-container {
    display: flex;
    flex-direction: column;

    #{$block-selector}__content--enter &,
    #{$block-selector}__content--leave-to & {
      opacity: 0;
      transform: translateY(2em);
    }

    #{$block-selector}__content--enter-active &,
    #{$block-selector}__content--leave-active & {
      transition-property: opacity, transform;
      transition-duration: $base-layout-content-transition-duration;
    }
  }

  &__footer {
    z-index: 400;
    grid-area: footer;

    @include breakpoint($base-layout-breakpoint) {
      overflow: hidden;
      grid-area: unset;
    }
  }

  @media all and (-ms-high-contrast: none) {
    display: -ms-grid;
    -ms-grid-columns: $base-layout-columns-l;
    -ms-grid-rows: $base-layout-rows;
    gap: $base-layout-gutter $base-layout-gutter;

    &__header {
      -ms-grid-row: 1;
      -ms-grid-row-span: 1;
      -ms-grid-column: 1;
      -ms-grid-column-span: 4;
    }

    &__sidebar {
      -ms-grid-row: 2;
      -ms-grid-row-span: 2;
      -ms-grid-column: 2;
      -ms-grid-column-span: 1;
    }

    &__content {
      -ms-grid-row: 2;
      -ms-grid-row-span: 1;
      -ms-grid-column: 3;
      -ms-grid-column-span: 2;
    }

    &__footer {
      -ms-grid-row: 3;
      -ms-grid-row-span: 1;
      -ms-grid-column: 3;
      -ms-grid-column-span: 1;
    }
  }
}
</style>
